export const BLOG_IMAGE_WIDTHS = Object.freeze(["small", "medium", "large", "full"]);
export const BLOG_IMAGE_ALIGNMENTS = Object.freeze(["left", "center", "right"]);

const imageWidthValues = new Set(BLOG_IMAGE_WIDTHS);
const imageAlignmentValues = new Set(BLOG_IMAGE_ALIGNMENTS);
const loopbackHosts = new Set(["localhost", "127.0.0.1", "[::1]"]);
const youtubeHosts = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "youtu.be",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
]);
const vimeoHosts = new Set(["vimeo.com", "www.vimeo.com", "player.vimeo.com"]);
const youtubeIdPattern = /^[A-Za-z0-9_-]{11}$/;
const vimeoIdPattern = /^\d{1,12}$/;

const cleanUrl = (value) => String(value || "").trim();

const isLoopbackHost = (hostname) =>
  loopbackHosts.has(hostname) || hostname.endsWith(".localhost");

const parseAbsoluteUrl = (value, { allowLoopbackHttp = false } = {}) => {
  const candidate = cleanUrl(value);
  if (
    !candidate ||
    candidate.length > 2048 ||
    /[\s<>"\\{}()]/.test(candidate)
  ) {
    return null;
  }

  try {
    const parsed = new URL(candidate);
    if (parsed.username || parsed.password) return null;
    if (parsed.protocol === "https:") return parsed;
    if (
      allowLoopbackHttp &&
      parsed.protocol === "http:" &&
      isLoopbackHost(parsed.hostname.toLowerCase())
    ) {
      return parsed;
    }
  } catch {
    return null;
  }

  return null;
};

export const normalizeBlogImageWidth = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return imageWidthValues.has(normalized) ? normalized : "full";
};

export const normalizeBlogImageAlignment = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return imageAlignmentValues.has(normalized) ? normalized : "center";
};

/**
 * Images must use HTTPS in published environments. Absolute HTTP URLs are
 * accepted only for loopback development servers.
 */
export const getSafeBlogImageUrl = (value) => {
  const parsed = parseAbsoluteUrl(value, { allowLoopbackHttp: true });
  return parsed ? parsed.toString() : "";
};

const youtubeIdFromUrl = (url) => {
  const host = url.hostname.toLowerCase();
  if (!youtubeHosts.has(host) || url.protocol !== "https:" || url.port) return "";

  if (host === "youtu.be") {
    const match = url.pathname.match(/^\/([^/]+)\/?$/);
    return match?.[1] || "";
  }

  if (url.pathname === "/watch") return url.searchParams.get("v") || "";

  const pathMatch = url.pathname.match(/^\/(?:embed|shorts)\/([^/]+)\/?$/);
  return pathMatch?.[1] || "";
};

const vimeoIdFromUrl = (url) => {
  const host = url.hostname.toLowerCase();
  if (!vimeoHosts.has(host) || url.protocol !== "https:" || url.port) return "";

  const expression =
    host === "player.vimeo.com" ? /^\/video\/(\d+)\/?$/ : /^\/(\d+)\/?$/;
  return url.pathname.match(expression)?.[1] || "";
};

/** Convert an allowlisted public video URL to a provider-controlled embed URL. */
export const resolveBlogVideo = (value) => {
  const parsed = parseAbsoluteUrl(value);
  if (!parsed || parsed.protocol !== "https:") return null;

  const youtubeId = youtubeIdFromUrl(parsed);
  if (youtubeIdPattern.test(youtubeId)) {
    return {
      provider: "YouTube",
      sourceUrl: `https://www.youtube.com/watch?v=${youtubeId}`,
      embedUrl: `https://www.youtube-nocookie.com/embed/${youtubeId}`,
    };
  }

  const vimeoId = vimeoIdFromUrl(parsed);
  if (vimeoIdPattern.test(vimeoId)) {
    return {
      provider: "Vimeo",
      sourceUrl: `https://vimeo.com/${vimeoId}`,
      embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
    };
  }

  return null;
};

const unescapeMediaText = (value) =>
  String(value || "").replace(/\\([\\\]"])/g, "$1");

const escapeAltText = (value) =>
  String(value || "")
    .replace(/\r?\n/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/\]/g, "\\]")
    .trim();

const escapeCaption = (value) =>
  String(value || "")
    .replace(/\r?\n/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .trim();

const parseImageAttributes = (source) => {
  const values = { width: "full", align: "center" };
  if (source == null) return values;

  const text = String(source).trim();
  if (!text) return null;
  const seen = new Set();
  for (const token of text.split(/\s+/)) {
    const match = token.match(/^(width|align)=([a-z]+)$/);
    if (!match || seen.has(match[1])) return null;
    seen.add(match[1]);
    if (match[1] === "width" && !imageWidthValues.has(match[2])) return null;
    if (match[1] === "align" && !imageAlignmentValues.has(match[2])) return null;
    values[match[1]] = match[2];
  }
  return values;
};

const imageExpression =
  /^!\[((?:\\.|[^\]\\])*)\]\((\S+?)(?:\s+"((?:\\.|[^"\\])*)")?\)(?:\{([^{}]+)\})?$/;
const videoExpression =
  /^@\[video\]\((\S+?)(?:\s+"((?:\\.|[^"\\])*)")?\)$/i;

export const parseBlogImageMarkdown = (line) => {
  const match = String(line || "").trim().match(imageExpression);
  if (!match) return null;

  const src = getSafeBlogImageUrl(match[2]);
  const attributes = parseImageAttributes(match[4]);
  if (!src || !attributes) return null;

  return {
    src,
    alt: unescapeMediaText(match[1]),
    caption: unescapeMediaText(match[3]),
    width: attributes.width,
    align: attributes.align,
  };
};

export const parseBlogVideoMarkdown = (line) => {
  const match = String(line || "").trim().match(videoExpression);
  if (!match) return null;

  const video = resolveBlogVideo(match[1]);
  if (!video) return null;
  return {
    url: video.sourceUrl,
    caption: unescapeMediaText(match[2]),
  };
};

export const serializeBlogImageMarkdown = (block = {}) => {
  const src = getSafeBlogImageUrl(block.url || block.src);
  if (!src) return "";

  const alt = escapeAltText(block.alt);
  const caption = escapeCaption(block.caption);
  const width = normalizeBlogImageWidth(block.width);
  const align = normalizeBlogImageAlignment(block.align);
  const attributes =
    width === "full" && align === "center"
      ? ""
      : `{width=${width} align=${align}}`;
  return `![${alt}](${src}${caption ? ` "${caption}"` : ""})${attributes}`;
};

export const serializeBlogVideoMarkdown = (block = {}) => {
  const video = resolveBlogVideo(block.url || block.src);
  if (!video) return "";

  const caption = escapeCaption(block.caption);
  return `@[video](${video.sourceUrl}${caption ? ` "${caption}"` : ""})`;
};

